import { useCallback } from 'react';
import { AdaptoActions } from '@frontend/adapto-actions';
import { Actions } from '@frontend/contact-actions';
import { useCustomContactFormSlidePanel } from '@frontend/create-contact-panel';
import { i18next, useTranslation } from '@frontend/i18n';
import { useMessagingFeaturesEnabled } from '@frontend/messaging-enabled-hook';
import { sanitizePhoneNumber } from '@frontend/phone-numbers';
import type { HydratedCallRecord } from '@frontend/phone-panels';
import { useContactPanelShallowStore } from '@frontend/shared';
import { useSlidePanelShallowStore } from '@frontend/slide-panel';
import { useAlert } from '@frontend/design-system';
import { getShortHandName } from '../call-recordings/call-recording-name-utils';
import { hasCallRecordingMedia } from '../call-recordings/call-recording-utils';

export type CallRecordActionsGetterProps<T extends HydratedCallRecord = HydratedCallRecord> = {
  index: number;
  rowData: T;
  isInsidePopoverDropdown?: boolean;
  addTag?: () => void;
};

export type CallRecordActionName =
  | 'call'
  | 'message'
  | 'viewProfile'
  | 'createContact'
  | 'addTag'
  | 'downloadRecording'
  | 'deleteRecording';

export type CallRecordActionProps = {
  trackingId?: string;
  trackingIdSuffix?: string;
  testId?: string;
};

export type UseCallRecordActionsGetterOptions<T extends HydratedCallRecord = HydratedCallRecord> = {
  downloadMedia: (rowData: T) => void;
  deleteMedia: (rowData: T) => void;
  downloadAccessMap?: Record<string, boolean>;
  deleteAccessMap?: Record<string, boolean>;
  hasDownloadAccess?: boolean;
  hasDeleteAccess?: boolean;
  onAfterAction?: (rowData: T, index: number) => void;
  onContactCreated?: () => void;
  disableMessageAction?: boolean;
  getActionProps?: (action: CallRecordActionName) => CallRecordActionProps | undefined;
};

export const useCallRecordActionsGetter = <T extends HydratedCallRecord = HydratedCallRecord>({
  downloadMedia,
  deleteMedia,
  downloadAccessMap,
  deleteAccessMap,
  hasDownloadAccess,
  hasDeleteAccess,
  onAfterAction = () => {},
  onContactCreated,
  disableMessageAction = false,
  getActionProps,
}: UseCallRecordActionsGetterOptions<T>): ((
  props: CallRecordActionsGetterProps<T>
) => (React.ReactElement | null)[]) => {
  const { setPersonId } = useContactPanelShallowStore('setPersonId', 'personId');
  const { setShow } = useSlidePanelShallowStore('setShow');

  const { t } = useTranslation('calls');
  const { setShow: openPanel } = useCustomContactFormSlidePanel();
  const alert = useAlert();
  const messagingEnabled = useMessagingFeaturesEnabled();

  return useCallback(
    ({ index, rowData, addTag, isInsidePopoverDropdown }: CallRecordActionsGetterProps<T>) =>
      getCallRecordActions({
        rowData,
        isInsidePopoverDropdown,
        disableMessageAction,
        viewProfile: () => {
          if (!!rowData.person?.personId) {
            setPersonId(rowData.person.personId, undefined, rowData.locationId);
            setShow(true, 'contact');
          }
        },
        createContact: () => {
          const number = rowData.direction === 'inbound' ? rowData.callerNumber : rowData.dialedNumber;

          const digitsOnly = number ? sanitizePhoneNumber(number) : '';
          const hasCountryCode = digitsOnly.startsWith('1');

          openPanel({
            show: true,
            context: {
              mode: 'create',
              person: {
                MobilePhone: hasCountryCode ? digitsOnly.slice(1) : digitsOnly,
              },
              locationId: rowData.locationId,
              onSave: () => {
                alert.success(t('Contact created successfully.'));
                onContactCreated?.();
              },
              onError: () => {
                alert.error(t('There was an error creating your contact. Please try again.'));
              },
            },
          });
        },
        addTag,
        downloadMedia: () => {
          downloadMedia(rowData);
        },
        deleteMedia: () => {
          deleteMedia(rowData);
        },
        hasDownloadAccess: downloadAccessMap
          ? Boolean(downloadAccessMap[rowData?.locationId ?? ''])
          : Boolean(hasDownloadAccess),
        hasDeleteAccess: deleteAccessMap
          ? Boolean(deleteAccessMap[rowData?.locationId ?? ''])
          : Boolean(hasDeleteAccess),
        hasMessagingAccess: messagingEnabled,
        getActionProps,
        onAfterAction: () => {
          onAfterAction(rowData, index);
        },
      }),
    [
      alert,
      deleteAccessMap,
      deleteMedia,
      downloadAccessMap,
      downloadMedia,
      hasDeleteAccess,
      hasDownloadAccess,
      messagingEnabled,
      onAfterAction,
      onContactCreated,
      openPanel,
      setPersonId,
      setShow,
      disableMessageAction,
      getActionProps,
      t,
    ]
  );
};

export type GetCallRecordActionsOptions = {
  rowData: HydratedCallRecord;
  viewProfile: () => void;
  createContact: () => void;
  isInsidePopoverDropdown?: boolean;
  addTag?: () => void;
  downloadMedia: () => void;
  deleteMedia: () => void;
  hasDownloadAccess: boolean;
  hasDeleteAccess: boolean;
  hasMessagingAccess?: boolean;
  disableMessageAction?: boolean;
  getActionProps?: (action: CallRecordActionName) => CallRecordActionProps | undefined;
  onAfterAction?: () => void;
};

export const getCallRecordActions = ({
  rowData,
  viewProfile,
  createContact,
  addTag,
  downloadMedia,
  deleteMedia,
  hasDownloadAccess,
  hasDeleteAccess,
  hasMessagingAccess = true,
  disableMessageAction = false,
  getActionProps,
  onAfterAction,
  isInsidePopoverDropdown,
}: GetCallRecordActionsOptions): (React.ReactElement | null)[] => {
  const { locationId, direction, callerNumber, dialedNumber } = rowData;
  const personId = rowData.personId || rowData.person?.personId;
  const hasMedia = hasCallRecordingMedia(rowData);
  const showDownloadMedia = hasMedia && hasDownloadAccess;
  const showDeleteMedia = hasMedia && hasDeleteAccess;
  // For inbound calls, the contact is the caller (callerNumber)
  // For outbound calls, the contact is the person dialed (dialedNumber)
  const contactPhoneNumber = direction === 'inbound' ? callerNumber : dialedNumber;

  return [
    <Actions.Call
      label={`${i18next.t('Call', { ns: 'calls' })} ${getShortHandName(rowData)}`}
      context={{
        personId,
        phoneNumber: contactPhoneNumber,
      }}
      trackingId={getActionProps?.('call')?.trackingId}
      trackingIdSuffix={getActionProps?.('call')?.trackingIdSuffix}
      key='call-action'
      onClick={onAfterAction}
      isInsidePopoverDropdown={isInsidePopoverDropdown}
    />,
    hasMessagingAccess ? (
      <Actions.Message
        disabled={disableMessageAction}
        label={`${i18next.t('Message', { ns: 'calls' })} ${getShortHandName(rowData)}`}
        context={{
          personId,
          threadGroupId: locationId,
          personPhone: contactPhoneNumber,
        }}
        trackingIdSuffix={getActionProps?.('message')?.trackingIdSuffix}
        data-testid={getActionProps?.('message')?.testId}
        key='message-action'
        onClick={onAfterAction}
      />
    ) : null,
    !!rowData.person?.personId ? (
      <AdaptoActions.Action
        icon='user'
        label={i18next.t('View Profile', { ns: 'calls' })}
        trackingId={getActionProps?.('viewProfile')?.trackingId}
        onClick={() => {
          viewProfile();
          onAfterAction?.();
        }}
        key='view-profile-action'
      />
    ) : (
      <AdaptoActions.Action
        icon='user-management'
        label={i18next.t('Create Contact', { ns: 'calls' })}
        trackingId={getActionProps?.('createContact')?.trackingId}
        onClick={() => {
          createContact();
          onAfterAction?.();
        }}
        key='create-contact-action'
      />
    ),
    addTag ? (
      <AdaptoActions.Action
        icon='label'
        label={i18next.t('Add Tag', { ns: 'calls' })}
        trackingId={getActionProps?.('addTag')?.trackingId}
        onClick={() => {
          addTag();
          onAfterAction?.();
        }}
        key='add-tag-action'
      />
    ) : null,
    showDownloadMedia ? (
      <AdaptoActions.Action
        icon='download-small'
        label={i18next.t('Download Recording', { ns: 'calls' })}
        trackingId={getActionProps?.('downloadRecording')?.trackingId}
        onClick={() => {
          downloadMedia();
          onAfterAction?.();
        }}
        key='download-media-action'
      />
    ) : null,
    showDeleteMedia ? (
      <AdaptoActions.Action
        icon='trash-small'
        label={i18next.t('Delete Recording', { ns: 'calls' })}
        trackingId={getActionProps?.('deleteRecording')?.trackingId}
        onClick={() => {
          deleteMedia();
          onAfterAction?.();
        }}
        destructive={true}
        key='delete-media-action'
      />
    ) : null,
  ];
};
